/**
 * Gives each publisher ad group an independently expandable, server-paginated
 * ad list with a custom flex header for full-width alignment control.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { useTranslation } from 'src/arb-wrapper';
import type {
  PaginatedMetaPaginationDocumented,
  PublisherEntityDto,
} from 'src/store/generated/leela-api-generated';
import { usePublisherEntitiesControllerListForCampaignQuery } from 'src/store/leela-api';
import { narrowAdGroupMetadata } from 'src/utils/publisherEntityMetadata';
import { EntityChildTable } from './EntityChildTable';
import { EntityIcon } from './EntityIcon';
import { EntityMetadataDetails } from './EntityMetadataDetails';
import {
  ADS_STATUS_FILTER_PARAMS,
  type AdsStatusFilter,
} from './statusFilterParams';
import {
  BADGE_LABELS,
  derivePublisherEntityStatus,
} from 'src/utils/publisherEntityStatus';
import { EMPTY_VALUE } from './StatusBadge';

const STATUS_TAG_TYPES: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  LIVE: 'success',
  PENDING: 'warning',
  FAILED: 'error',
  SKIPPED: 'neutral',
  REMOVED: 'neutral',
};

const ADS_PAGE_SIZE = 25;
const EMPTY_ADS_PAGINATION: PaginatedMetaPaginationDocumented = {
  page: 1,
  pageCount: 1,
  pageSize: ADS_PAGE_SIZE,
  total: 0,
};

interface EntityGroupRowProps {
  campaignId: string;
  entity: PublisherEntityDto;
}

export const EntityGroupRow: React.FC<EntityGroupRowProps> = ({
  campaignId,
  entity,
}) => {
  const { t } = useTranslation();
  const [hasExpanded, setHasExpanded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<AdsStatusFilter>('LIVE');
  const [selectedAdId, setSelectedAdId] = useState<string | undefined>();
  const metadata = narrowAdGroupMetadata(entity.publisherMetadata);
  const query = usePublisherEntitiesControllerListForCampaignQuery({
    parentPublisherEntityId: entity.publisherEntityId,
    campaignId,
    page,
    pageSize: ADS_PAGE_SIZE,
    publisher: 'google',
    publisherEntityType: 'AD',
    ...ADS_STATUS_FILTER_PARAMS[statusFilter],
  }, { skip: !hasExpanded });

  useEffect(() => {
    const pageCount = query.data?.meta.pagination.pageCount;
    if (pageCount !== undefined && pageCount < page) {
      setPage(Math.max(1, pageCount));
    }
  }, [page, query.data?.meta.pagination.pageCount]);

  const handleToggle = (open: boolean) => {
    if (!hasExpanded) setHasExpanded(true);
    setIsExpanded(open);
  };

  const handleStatusFilterChange = (value: AdsStatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleSelectAd = useCallback((ad: PublisherEntityDto) => {
    setSelectedAdId(prev => prev === ad._id ? undefined : ad._id);
  }, []);

  const adCount = query.data?.meta?.pagination?.total;
  const status = derivePublisherEntityStatus(entity);

  return (
    <Column data-testid={`ads-manager-ad-group-row-${entity._id}`} spacing="none">
      <div
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px 0' }}
        onClick={() => { handleToggle(!isExpanded); }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
          <EntityIcon size={32} type={entity.publisherEntityType} />
          <Column spacing="none">
            <Text type="b300">{metadata?.name ?? EMPTY_VALUE}</Text>
            <Text color="secondary" type="b100">{entity.publisherEntityId ?? EMPTY_VALUE}</Text>
          </Column>
          {adCount != null && adCount > 0 && (
            <Text color="secondary" type="b100">{t('ads_manager_ad_count', { count: adCount })}</Text>
          )}
        </div>
        <div style={{ minWidth: '90px', textAlign: 'right', flexShrink: 0 }}>
          <Tag type={STATUS_TAG_TYPES[status]}>{t(BADGE_LABELS[status])}</Tag>
        </div>
      </div>
      {isExpanded && (
      <Box
        backgroundColor="subdued"
        data-testid={`ads-manager-ad-group-panel-${entity._id}`}
        spacingInset="400"
      >
        <Column spacing="400">
          <EntityMetadataDetails
            layout="row"
            metadata={(() => {
              const meta = entity.publisherMetadata as Record<string, unknown> | undefined;
              if (!meta) return undefined;
              const { name: _n, useAudienceGrouped: _u, ...rest } = meta;
              // Remove useAudienceGrouped if it's falsy/undefined
              const filtered = Object.fromEntries(
                Object.entries(rest).filter(([, v]) => v !== undefined && v !== null),
              );
              return Object.keys(filtered).length > 0 ? filtered : undefined;
            })()}
            testId={`ads-manager-ad-group-metadata-${entity._id}`}
          />
          {query.isError
            ? (
              <Alert
                data-testid={`ads-manager-ad-group-query-error-${entity._id}`}
                type="error"
              >
                {t('ads_manager_ad_group_query_error')}
              </Alert>
              )
            : (
              <EntityChildTable
                campaignId={campaignId}
                data={(query.data?.data ?? []) as PublisherEntityDto[]}
                isError={query.isError}
                isFetching={query.isFetching}
                isLoading={query.isLoading}
                page={page}
                pagination={query.data?.meta.pagination ?? EMPTY_ADS_PAGINATION}
                selectedAdId={selectedAdId}
                statusFilter={statusFilter}
                onPageChange={setPage}
                onSelectAd={handleSelectAd}
                onStatusFilterChange={handleStatusFilterChange}
              />
              )}
        </Column>
      </Box>
      )}
    </Column>
  );
};
